/**
 * Deep-link bridge for the Compare Sales modal - the compare analog of the
 * exception drawer's `?exceptionObject` deep-link.
 *
 * Renders nothing; it is a render-gated child that the *owner* host mounts
 * (the data/sales table, or the exception / admin drawer that launches
 * compare). The URL carries a single short param - `?compareLsSale=<lsId>`.
 * Two responsibilities:
 *
 *   - READ: when the URL carries `?compareLsSale` and the host knows its
 *     `fpCode`, open the modal once. fpCode comes from the host (the inspector
 *     route / table already has it) rather than the URL, keeping the link
 *     short; the compare endpoint re-resolves the Xero counterpart from the
 *     sale itself, so no xero id is needed either. The modal's own fetch fills
 *     the body + header (CompareSalesDialog's `target.X ?? viewData.X`).
 *
 *   - WRITE: mirror the open modal into the URL (`replace`, like the drawer)
 *     so it is shareable, and clear it on close.
 *
 * Mounted only by the single deep-link owner per page (gated by the host on
 * `rowDetail.type === 'compare-sales'` / a `compareDeepLink` prop), so two
 * hosts never both auto-open from one URL. Render-gated, so `useSearchParams`
 * only runs inside a Router.
 */
import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { CompareSalesTarget } from '../../../hooks/useCompareSalesDialog'

interface CompareSalesUrlSyncProps {
  /**
   * The host's franchise code, used to build the target on a cold load.
   * Inspector hosts always have it (route / table); cross-FP admin hosts only
   * have it once a row is selected, so an admin cold-load simply won't
   * auto-open (the param is still written + shareable within a session).
   */
  fpCode?: string
  /** Current modal target (null when closed) - the hook's `dialogProps.target`. */
  target: CompareSalesTarget | null
  /** The hook's `open` - called with the minimal target on a cold/shared load. */
  onOpen: (target: CompareSalesTarget) => void
}

const PARAM = 'compareLsSale'

const CompareSalesUrlSync: React.FC<CompareSalesUrlSyncProps> = ({
  fpCode,
  target,
  onOpen,
}) => {
  const [search, setSearchParams] = useSearchParams()
  const urlSale = search.get(PARAM)

  // READ - open once per distinct param value. `handledRef` is the fix for the
  // close-then-reopen race: when the user closes, `target` flips to null while
  // the param is briefly still present (the WRITE effect clears it on the next
  // tick). Without this guard the effect would treat that as "param present,
  // modal closed -> open it" and immediately reopen. We mark a param value
  // handled the moment we open it (or find the modal already open), and only
  // reset when the param actually leaves the URL.
  const handledRef = useRef<string | null>(null)
  useEffect(() => {
    if (!urlSale) {
      handledRef.current = null
      return
    }
    if (handledRef.current === urlSale) return
    if (target) {
      // User opened it (or we already did); record so a close doesn't reopen.
      handledRef.current = urlSale
      return
    }
    if (!fpCode) return // can't build the target yet; retry when fpCode lands
    handledRef.current = urlSale
    onOpen({ fpCode, lsId: urlSale })
    // onOpen is a stable useCallback from the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSale, target, fpCode])

  // WRITE - keep the URL in step with the modal. `everOpenedRef` lets the
  // initial cold-load pass (param present, target not yet hydrated) skip the
  // clear, while a genuine user close (target null after having been open)
  // still clears. Comparing against `urlSale` makes the hydrate + steady state
  // no-ops, so there is no write loop.
  const everOpenedRef = useRef(false)
  useEffect(() => {
    if (target) everOpenedRef.current = true
    const wantSale = target?.lsId ?? null
    if (wantSale === urlSale) return
    if (!target && !everOpenedRef.current) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (target) next.set(PARAM, target.lsId)
        else next.delete(PARAM)
        return next
      },
      { replace: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, urlSale])

  return null
}

export default CompareSalesUrlSync
